import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faFolder,
  faFile,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { useQuery } from 'react-query';
import {
  SHAREPOINT_API,
  SharePointFile,
  SharePointFolder,
  formatFileSize,
  getFileIcon,
  isFilePreviewable,
  downloadSharePointFile,
  openFilePreview,
  getAllFoldersFromResults,
  getAllFilesFromResults,
  getFoundJobResults,
  getNotFoundJobNumbers
} from 'services/api/sharepoint';
import HumanReadableTime from 'components/utils/HumanReadableTime';
import { SharePointModalProps } from './types/types';

const SharePointModal: React.FC<SharePointModalProps> = ({
  show,
  onHide,
  jobNumbers,
  companyNames
}) => {
  const primaryJobNumber =
    jobNumbers && jobNumbers.length > 0 ? jobNumbers[0] : '';
  const [selectedFolder, setSelectedFolder] = useState<SharePointFolder | null>(
    null
  );
  const [folderPath, setFolderPath] = useState<SharePointFolder[]>([]);

  const {
    data: jobData,
    isLoading: isLoadingJob,
    error: jobError,
    refetch: refetchJob
  } = useQuery(
    ['sharepoint-job', jobNumbers, companyNames],
    () => SHAREPOINT_API.GET_JOB_FOLDERS(jobNumbers, companyNames),
    {
      enabled: show && !!jobNumbers,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false
    }
  );

  const {
    data: folderDetails,
    isLoading: isLoadingFolder,
    error: folderError,
    refetch: refetchFolder
  } = useQuery(
    ['sharepoint-folder', selectedFolder?.id],
    () =>
      selectedFolder
        ? SHAREPOINT_API.GET_FOLDER_DETAILS(selectedFolder.id)
        : Promise.reject('No folder selected'),
    {
      enabled: show && !!selectedFolder?.id,
      staleTime: 2 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false
    }
  );

  useEffect(() => {
    if (!show) {
      setSelectedFolder(null);
      setFolderPath([]);
    }
  }, [show, jobNumbers]);

  // Auto-navigate to first job folder when data loads
  useEffect(() => {
    if (jobData && !selectedFolder && !isLoadingJob) {
      const foundResults = getFoundJobResults(jobData.results || []);
      const primaryResult = foundResults?.find(
        result => result.jobNumber === primaryJobNumber
      );

      if (
        primaryResult &&
        primaryResult.folders &&
        primaryResult.folders.length > 0
      ) {
        // Auto-select the first job folder
        const firstFolder = primaryResult.folders[0];
        setSelectedFolder(firstFolder);
        setFolderPath([firstFolder]);
      }
    }
  }, [jobData, selectedFolder, isLoadingJob, primaryJobNumber]);

  const handleFolderClick = (folder: SharePointFolder) => {
    setSelectedFolder(folder);
    setFolderPath(prev => [...prev, folder]);
  };

  const handleBackToRoot = () => {
    setSelectedFolder(null);
    setFolderPath([]);
  };

  const handleBackToParent = () => {
    if (folderPath.length > 1) {
      const newPath = folderPath.slice(0, -1);
      const parentFolder = newPath[newPath.length - 1];
      setSelectedFolder(parentFolder);
      setFolderPath(newPath);
    } else {
      handleBackToRoot();
    }
  };

  const handleDownload = async (file: SharePointFile) => {
    try {
      await downloadSharePointFile(file);
    } catch (error) {
      console.error('Failed to download file:', error);
    }
  };

  const handlePreview = async (file: SharePointFile) => {
    try {
      await openFilePreview(file);
    } catch (error) {
      console.error('Failed to preview file:', error);
      alert(
        `Unable to preview "${file.name}". Please try downloading the file instead.`
      );
    }
  };

  const renderBreadcrumbs = () => {
    const foundResults = jobData?.results
      ? getFoundJobResults(jobData.results)
      : [];
    const primaryResult = foundResults?.find(
      result => result.jobNumber === primaryJobNumber
    );
    const company = primaryResult ? primaryResult.companyName : '';

    return (
      <>
        <span className="text-muted">Company: {company}</span>
        <FontAwesomeIcon icon={faTimes} className="mx-2 text-muted" />
        <span className="text-muted">Job: {primaryJobNumber}</span>
        {folderPath.map((folder, index) => (
          <React.Fragment key={folder.id}>
            <FontAwesomeIcon icon={faTimes} className="mx-2 text-muted" />
            <span
              className={
                index === folderPath.length - 1
                  ? 'text-primary'
                  : 'text-muted cursor-pointer'
              }
              style={{
                cursor: index < folderPath.length - 1 ? 'pointer' : 'default'
              }}
              onClick={() => {
                if (index < folderPath.length - 1) {
                  const newPath = folderPath.slice(0, index + 1);
                  const targetFolder = newPath[newPath.length - 1];
                  setSelectedFolder(targetFolder);
                  setFolderPath(newPath);
                }
              }}
            >
              {folder.name}
            </span>
          </React.Fragment>
        ))}
      </>
    );
  };

  const renderContent = () => {
    if (isLoadingJob) {
      return (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Loading SharePoint content...</p>
        </div>
      );
    }

    if (jobError) {
      return (
        <Alert variant="danger">
          <Alert.Heading>Error Loading Job Data</Alert.Heading>
          <p>Failed to load SharePoint content. Please try again.</p>
          <Button
            onClick={() => refetchJob()}
            variant="outline-danger"
            size="sm"
          >
            Retry
          </Button>
        </Alert>
      );
    }

    if (!jobData || !jobData.results || jobData.results.length === 0) {
      return (
        <div className="text-center py-5">
          <FontAwesomeIcon
            icon={faFolder}
            className="text-muted mb-3"
            size="3x"
          />
          <h5 className="text-muted">No items found</h5>
          <p className="text-muted">
            No SharePoint content found for this job.
          </p>
        </div>
      );
    }

    const foundResults = getFoundJobResults(jobData.results || []);
    const notFoundJobNumbers = getNotFoundJobNumbers(jobData.results || []);

    if (foundResults.length === 0) {
      return (
        <div className="text-center py-5">
          <FontAwesomeIcon
            icon={faFolder}
            className="text-muted mb-3"
            size="3x"
          />
          <h5 className="text-muted">No items found</h5>
          <p className="text-muted">
            No SharePoint content found for these job numbers.
          </p>
          {notFoundJobNumbers.length > 0 && (
            <div className="mt-3">
              <small className="text-muted">
                Job numbers not found: {notFoundJobNumbers.join(', ')}
              </small>
            </div>
          )}
        </div>
      );
    }

    const combinedJobContent = {
      folders: getAllFoldersFromResults(foundResults),
      files: getAllFilesFromResults(foundResults)
    };

    if (selectedFolder) {
      if (isLoadingFolder) {
        return (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading folder contents...</p>
          </div>
        );
      }

      if (folderError) {
        return (
          <Alert variant="danger">
            <Alert.Heading>Error Loading Folder Contents</Alert.Heading>
            <p>Failed to load folder contents. Please try again.</p>
            <Button
              onClick={() => refetchFolder()}
              variant="outline-danger"
              size="sm"
            >
              Retry
            </Button>
          </Alert>
        );
      }

      if (folderDetails) {
        return (
          <>
            {/* Folder Header - Enhanced styling to match screenshot */}
            <div className="text-center py-4 mb-4">
              <FontAwesomeIcon
                icon={faFolder}
                className="text-warning mb-3"
                size="4x"
              />
              <h4 className="text-primary mb-3">
                {folderDetails.currentFolder.name}
              </h4>
              <div className="row justify-content-center">
                <div className="col-md-8">
                  <div className="d-flex justify-content-center gap-4 mb-3">
                    <div className="text-center">
                      <small className="text-muted d-block">Created by</small>
                      <span className="fw-medium">
                        {folderDetails.currentFolder.createdBy.user.displayName}
                      </span>
                    </div>
                    <div className="text-center">
                      <small className="text-muted d-block">Items</small>
                      <span className="fw-medium">
                        {folderDetails.currentFolder.childCount}
                      </span>
                    </div>
                    <div className="text-center">
                      <small className="text-muted d-block">
                        Last modified
                      </small>
                      <span className="fw-medium">
                        <HumanReadableTime
                          date={
                            new Date(
                              folderDetails.currentFolder.lastModifiedDateTime
                            )
                          }
                        />
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() =>
                      window.open(folderDetails.currentFolder.webUrl, '_blank')
                    }
                    className="mb-3"
                  >
                    Open in SharePoint
                  </Button>
                </div>
              </div>
            </div>

            {/* Subfolders - Enhanced styling */}
            {folderDetails.folders && folderDetails.folders.length > 0 && (
              <div className="mb-4">
                <h6 className="text-muted mb-3">
                  <FontAwesomeIcon
                    icon={faFolder}
                    className="me-2 text-warning"
                  />
                  Subfolders ({folderDetails.folders.length})
                </h6>
                <Row className="g-3">
                  {folderDetails.folders.map((folder: SharePointFolder) => (
                    <Col xs={12} sm={6} md={4} lg={3} key={folder.id}>
                      <div
                        className="card h-100 cursor-pointer hover-shadow border-0 shadow-sm"
                        onClick={() => handleFolderClick(folder)}
                        style={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        <div className="card-body text-center p-4">
                          <FontAwesomeIcon
                            icon={faFolder}
                            className="text-warning mb-3"
                            size="3x"
                          />
                          <h6 className="mb-2 text-primary" title={folder.name}>
                            {folder.name}
                          </h6>
                          <small className="text-muted d-block mb-2">
                            {folder.childCount} items
                          </small>
                          <small className="text-muted">
                            <HumanReadableTime
                              date={new Date(folder.lastModifiedDateTime)}
                            />
                          </small>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            )}

            {/* Files in the folder - Enhanced styling to match screenshot */}
            {folderDetails.files && folderDetails.files.length > 0 && (
              <div>
                <h6 className="text-muted mb-3">
                  <FontAwesomeIcon icon={faFile} className="me-2 text-info" />
                  Files ({folderDetails.files.length})
                </h6>
                <Row className="g-3">
                  {folderDetails.files.map((file: SharePointFile) => (
                    <Col xs={12} sm={6} md={4} lg={3} key={file.id}>
                      <div className="card h-100 border shadow-sm">
                        <div className="card-body p-4">
                          <div className="text-center mb-3">
                            <FontAwesomeIcon
                              icon={getFileIcon(file.fileType) as any}
                              className="text-info"
                              size="2x"
                            />
                          </div>
                          <h6 className="mb-2 text-truncate" title={file.name}>
                            {file.name}
                          </h6>
                          <small className="text-muted d-block mb-3">
                            {formatFileSize(file.size)}
                          </small>
                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                              <HumanReadableTime
                                date={new Date(file.lastModifiedDateTime)}
                              />
                            </small>
                            <div className="btn-group btn-group-sm">
                              {isFilePreviewable(file.fileType) && (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handlePreview(file)}
                                  title="Preview"
                                >
                                  <FontAwesomeIcon icon={faFile} />
                                </Button>
                              )}
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleDownload(file)}
                                title="Download"
                              >
                                <FontAwesomeIcon icon={faFile} />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            )}

            {/* Empty folder message */}
            {(!folderDetails.folders || folderDetails.folders.length === 0) &&
              (!folderDetails.files || folderDetails.files.length === 0) && (
                <div className="text-center py-4">
                  <FontAwesomeIcon
                    icon={faFolder}
                    className="text-muted mb-3"
                    size="3x"
                  />
                  <h6 className="text-muted">Empty Folder</h6>
                  <p className="text-muted">This folder contains no items.</p>
                </div>
              )}
          </>
        );
      }

      return (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Loading folder contents...</p>
        </div>
      );
    }

    return (
      <>
        {/* Job Folders - Display directly like in the screenshot */}
        {combinedJobContent.folders &&
          combinedJobContent.folders.length > 0 && (
            <div className="mb-4">
              <h6 className="text-muted mb-3">
                <FontAwesomeIcon
                  icon={faFolder}
                  className="me-2 text-warning"
                />
                Job Folders ({combinedJobContent.folders.length})
              </h6>
              <Row className="g-3">
                {combinedJobContent.folders.map((folder: SharePointFolder) => (
                  <Col xs={12} sm={6} md={4} lg={3} key={folder.id}>
                    <div
                      className="card h-100 cursor-pointer hover-shadow border-0 shadow-sm"
                      onClick={() => handleFolderClick(folder)}
                      style={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      <div className="card-body text-center p-4">
                        <FontAwesomeIcon
                          icon={faFolder}
                          className="text-warning mb-3"
                          size="3x"
                        />
                        <h6 className="mb-2 text-primary" title={folder.name}>
                          {folder.name}
                        </h6>
                        <small className="text-muted d-block mb-2">
                          {folder.childCount} items
                        </small>
                        <small className="text-muted">
                          {folder.childCount} items
                        </small>
                        <div className="mt-2">
                          <small className="text-muted">
                            <HumanReadableTime
                              date={new Date(folder.lastModifiedDateTime)}
                            />
                          </small>
                        </div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          )}

        {/* Files - Only show if there are files at the root level */}
        {combinedJobContent.files && combinedJobContent.files.length > 0 && (
          <div>
            <h6 className="text-muted mb-3">
              <FontAwesomeIcon icon={faFile} className="me-2 text-info" />
              Job Files ({combinedJobContent.files.length})
            </h6>
            <Row className="g-3">
              {combinedJobContent.files.map((file: SharePointFile) => (
                <Col xs={12} sm={6} md={4} lg={3} key={file.id}>
                  <div className="card h-100 border shadow-sm">
                    <div className="card-body p-4">
                      <div className="text-center mb-3">
                        <FontAwesomeIcon
                          icon={getFileIcon(file.fileType) as any}
                          className="text-info"
                          size="2x"
                        />
                      </div>
                      <h6 className="mb-2 text-truncate" title={file.name}>
                        {file.name}
                      </h6>
                      <small className="text-muted d-block mb-3">
                        {formatFileSize(file.size)}
                      </small>
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          <HumanReadableTime
                            date={new Date(file.lastModifiedDateTime)}
                          />
                        </small>
                        <div className="btn-group btn-group-sm">
                          {isFilePreviewable(file.fileType) && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handlePreview(file)}
                              title="Preview"
                            >
                              <FontAwesomeIcon icon={faFile} />
                            </Button>
                          )}
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handleDownload(file)}
                            title="Download"
                          >
                            <FontAwesomeIcon icon={faFile} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faFolder} className="me-2 text-primary" />
          SharePoint Files
          {primaryJobNumber && (
            <span className="text-muted ms-2">- Job {primaryJobNumber}</span>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Navigation */}
        {jobNumbers && (
          <div className="mb-3">
            {selectedFolder && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleBackToParent}
                className="mb-2"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                {folderPath.length > 1
                  ? 'Back to Parent'
                  : 'Back to Company View'}
              </Button>
            )}
            <div className="text-muted small">{renderBreadcrumbs()}</div>
          </div>
        )}

        {/* Job Results Summary */}
        {jobData && !selectedFolder && (
          <div className="mb-3 p-3 bg-light rounded">
            <div className="row">
              <div className="col-md-6">
                <h6 className="mb-2">Search Summary</h6>
                <div className="small text-muted">
                  <div>
                    Total Job Numbers: {jobData.summary?.totalJobNumbers || 0}
                  </div>
                  <div>Found: {jobData.summary?.foundJobNumbers || 0}</div>
                  <div>
                    Not Found:{' '}
                    {jobData.summary?.notFoundJobNumbers?.length || 0}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <h6 className="mb-2">Content Overview</h6>
                <div className="small text-muted">
                  <div>
                    Total Folders:{' '}
                    {getAllFoldersFromResults(jobData.results || []).length}
                  </div>
                  <div>
                    Total Files:{' '}
                    {getAllFilesFromResults(jobData.results || []).length}
                  </div>
                </div>
              </div>
            </div>
            {jobData.summary?.notFoundJobNumbers?.length > 0 && (
              <div className="mt-2">
                <small className="text-warning">
                  <strong>Not Found:</strong>{' '}
                  {jobData.summary?.notFoundJobNumbers?.join(', ')}
                </small>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        {renderContent()}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SharePointModal;
